import { getRouteProjectDetails, getRouteProjects } from "@/zapi-frontend/shared/const/router";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CircularProgress, Typography } from "@libs/zapi-ui/custom";
import { useCallback, useEffect, useState } from "react";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { GET_PROJECTS_LIST_KEY } from "@/zapi-frontend/shared/services/useGetProjectsList";
import { useCreateProjectStore } from "@/zapi-frontend/stores/createProject";
import { TCreateProjectStep } from "@/zapi-frontend/shared/types/createProjStep";
import { CreateProjectForm, newProjectSchema, TMinFileOrFile } from "@/zapi-frontend/widgets/CreateProjectForm";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import $api from "@/zapi-frontend/shared/api/axios";
import { useProjectSetupStore } from "@/zapi-frontend/stores/projectSetup";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { TModelType } from "@/zapi-frontend/shared/types/models";
import { useChatStore } from "@/zapi-frontend/stores/chatStore";
import { useProjectSetupPolling } from "@/zapi-frontend/shared/hooks/useProjectSetupPolling";
import { usePreUploadDocuments } from "../model/usePreUploadDocuments";

const triggerAutoSetup = async (projectId: string, model?: string, prefill = true): Promise<number | null> => {
  try {
    const response = await $api.post<{ taskId: number }>(`/projects/${projectId}/auto-setup`, {
      model,
      prefill
    });
    return response.data.taskId;
  } catch (error) {
    console.error("Failed to trigger auto-setup:", error);
    throw error;
  }
};

export function CreateProjectPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { addProject } = useProjectStore();
  const { setSetupTask } = useProjectSetupStore();
  const {
    uploadDocuments,
    addWebLink,
    uploadedDocuments,
    clearDocuments,
    isUploading: isUploadingDocs
  } = usePreUploadDocuments();

  const {
    projectData,
    errors,
    setProjectData,
    createdProjectId,
    setCreatedProjectId,
    setErrors,
    setIsLoading,
    isLoading,
    clearForm,
    reset
  } = useCreateProjectStore();

  const { chapterMemoPolling } = useProjectSetupPolling(
    createdProjectId ? String(createdProjectId) : "",
    null,
    !!createdProjectId,
    undefined
  );

  const step = (searchParams.get("step") as TCreateProjectStep) ?? "details";

  useEffect(() => {
    if (!searchParams.get("step")) {
      const params = new URLSearchParams(searchParams);
      params.set("step", "details");
      setSearchParams(params, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const [logo] = useState<string | null>(null);
  const [files, setFiles] = useState<TMinFileOrFile[]>([]);
  const { modelName: selectedModel, setModelName: setSelectedModel } = useChatStore();
  const { isLabs } = useMeStore();
  const [skipPrefill, setSkipPrefill] = useState(false);
  const [uploadingFilesCount, setUploadingFilesCount] = useState(0);

  const handleFileAdd = async (newFiles: TMinFileOrFile[]) => {
    const fileObjects = newFiles.filter((f) => f instanceof File) as File[];

    if (fileObjects.length > 0) {
      try {
        setUploadingFilesCount(fileObjects.length);
        const uploadedMinFiles = await uploadDocuments(fileObjects);
        setFiles((prev) => [...prev, ...uploadedMinFiles]);
      } catch (error) {
        console.error("Failed to upload files:", error);
        toast.error("Failed to upload files");
      } finally {
        setUploadingFilesCount(0);
      }
    }
  };

  const handleWebLinkAdd = async (url: string): Promise<TMinFileOrFile | void> => {
    try {
      const minFile = await addWebLink(url);
      setFiles((prev) => [...prev, minFile]);
      return minFile;
    } catch (error) {
      console.error("Failed to add web link:", error);
      throw error;
    }
  };

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };

    if (step === "details" && (files.length > 0 || logo !== null)) {
      window.addEventListener("beforeunload", handleBeforeUnload);
    }

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [step, files.length, logo]);

  const handleResetFromQuery = useCallback(() => {
    const shouldReset = searchParams.get("reset") === "true";
    if (shouldReset) {
      reset();
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete("reset");
      newSearchParams.set("step", "details");
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, reset]);

  useEffect(() => {
    handleResetFromQuery();
  }, [handleResetFromQuery]);

  const calculateTotalCharacterCount = (data: typeof projectData): number => {
    const description = data.description || "";
    const projectInstructions = data.projectInstructions || "";
    const decisionContext = data.decisionContext || "";
    const additionalContext = data.additionalContext || "";
    const targetAudience = data.targetAudience || "";
    const successMetrics = data.successMetrics || "";

    return (
      description.length +
      projectInstructions.length +
      decisionContext.length +
      additionalContext.length +
      targetAudience.length +
      successMetrics.length
    );
  };

  const handleCreateAndUpload = async () => {
    const validationResult = newProjectSchema.safeParse(projectData);
    if (!validationResult.success) {
      const validationErrors = Object.fromEntries(
        Object.entries(validationResult.error.flatten().fieldErrors).map(([key, errors]) => [key, errors[0]])
      );
      setErrors(validationErrors);
      toast.error("Invalid project data");
      return;
    }

    try {
      setIsLoading(true);

      let documentIds: number[] = [];

      if (uploadedDocuments.length > 0) {
        documentIds = uploadedDocuments.map((doc) => doc.documentId);
      }

      const newId = await addProject({
        name: validationResult.data.name,
        projectTemplateSlug: validationResult.data.projectTemplateSlug,
        description: validationResult.data.description,
        projectInstructions: validationResult.data.projectInstructions,
        language: validationResult.data.language,
        chaptersInstructions: validationResult.data.chaptersInstructions,
        logoBase64: logo || undefined,
        decisionContext: validationResult.data.decisionContext,
        successMetrics: validationResult.data.successMetrics,
        targetAudience: validationResult.data.targetAudience,
        additionalContext: validationResult.data.additionalContext,
        documentIds: documentIds.length > 0 ? documentIds : undefined,
        presentationPrompt: validationResult.data.presentationPrompt
      });

      setCreatedProjectId(+newId);
      queryClient.invalidateQueries({ queryKey: [GET_PROJECTS_LIST_KEY], refetchType: "all" });

      const totalCharCount = calculateTotalCharacterCount(validationResult.data);
      const hasDocuments = documentIds.length > 0;
      const shouldTriggerAutoSetup = hasDocuments || totalCharCount >= 250;

      if (shouldTriggerAutoSetup) {
        const taskId = await triggerAutoSetup(newId, selectedModel, !skipPrefill);
        if (taskId) {
          setSetupTask(newId, taskId);
        }
      }

      clearForm();
      clearDocuments();
      const shouldShowModal = shouldTriggerAutoSetup && !skipPrefill;
      navigate(`${getRouteProjectDetails(newId)}?view=dashboard${shouldShowModal ? "&showGenerationModal=true" : ""}`);
    } catch (error) {
      console.error(error);
      toast.error("Failed to create project");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className={cn(
        "flex bg-innoveraZinc-100 gap-2 h-[calc(100vh-56px)] w-full p-2 overflow-hidden pl-[70px] relative"
      )}
    >
      <div
        data-testid="sidebar-chapters-root"
        className={cn(
          "absolute top-2 left-2 z-10 w-[56px] h-[calc(100vh-72px)] flex flex-col items-center py-3 px-1 bg-white transition-all duration-300"
        )}
      >
        <div
          data-testid="sidebar-chapters-list"
          className={cn("flex flex-col gap-1 w-full overflow-y-auto h-full", "custom-thin-scrollbar")}
        >
          {(chapterMemoPolling.chapters.length > 0 ? chapterMemoPolling.chapters : [1, 2, 3, 4]).map(
            (chapter, index) => {
              const isChapterData = typeof chapter === "object";
              const hasActiveMemo = isChapterData ? chapter.hasActiveMemo : false;
              const isActive = index === 0;
              return (
                <div
                  className="w-full flex flex-col items-center justify-center"
                  key={isChapterData ? chapter.id : index}
                >
                  <div className={cn("w-full flex justify-start items-center cursor-pointer group", "mb-3")}>
                    <div
                      className={cn(
                        "size-10 min-w-10 rounded-full flex justify-center items-center ml-1 transition-colors duration-300",
                        isActive ? "bg-innoveraRed-50 group-hover:bg-innoveraRed-50" : "bg-innoveraZinc-100"
                      )}
                    >
                      <div className={`size-8 relative flex justify-center items-center`}>
                        <CircularProgress
                          withoutCheckMark
                          progress={hasActiveMemo ? 20 : 0}
                          max={20}
                          size={32}
                          strokeWidth={1.5}
                          color={
                            hasActiveMemo
                              ? "var(--innovera-green-500)"
                              : isActive
                              ? "var(--innovera-orange400)"
                              : "var(--innovera-zinc200)"
                          }
                          trackColor={isActive ? "var(--innovera-orange300)" : "transparent"}
                          loading={false}
                        />
                        {isActive && (
                          <div className="absolute inset-0 flex justify-center items-center">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="none"
                            >
                              <path
                                d="M13.7069 6.79266L8.70688 1.79266C8.51936 1.60527 8.26511 1.5 8.00001 1.5C7.7349 1.5 7.48065 1.60527 7.29313 1.79266L2.29313 6.79266C2.19982 6.88528 2.12586 6.99552 2.07553 7.11698C2.02521 7.23844 1.99954 7.36869 2.00001 7.50016V13.5002C2.00001 13.6328 2.05268 13.7599 2.14645 13.8537C2.24022 13.9475 2.3674 14.0002 2.50001 14.0002H13.5C13.6326 14.0002 13.7598 13.9475 13.8536 13.8537C13.9473 13.7599 14 13.6328 14 13.5002V7.50016C14.0005 7.36869 13.9748 7.23844 13.9245 7.11698C13.8742 6.99552 13.8002 6.88528 13.7069 6.79266ZM13 13.0002H3.00001V7.50016L8.00001 2.50016L13 7.50016V13.0002Z"
                                fill="#FF603E"
                              />
                            </svg>
                          </div>
                        )}
                      </div>
                    </div>

                    <Typography
                      variant="label2"
                      color={isActive ? "zinc900" : "zinc300"}
                      className={cn(
                        "font-medium max-w-[200px] truncate ml-2 transition-colors duration-300",
                        isActive && "group-hover:text-innoveraZinc-900"
                      )}
                      style={{ lineHeight: "120%" }}
                    >
                      "—"
                    </Typography>
                  </div>
                </div>
              );
            }
          )}
        </div>
        <div className="mt-auto mr-auto"></div>
      </div>
      <div className="flex flex-col w-full h-full bg-white overflow-hidden">
        <div
          className={cn(
            "grid w-full grid-cols-[1fr_0px] h-full transition-all duration-300",
            false && "grid-cols-[1fr_420px]"
          )}
        >
          <div className="w-full h-full flex overflow-y-auto min-h-0">
            <div className="flex flex-col gap-2 w-full px-5 min-[1025px]:px-[236px]">
              <CreateProjectForm
                projectData={projectData}
                setProjectData={setProjectData}
                errors={errors}
                setErrors={setErrors}
                files={files}
                setFiles={setFiles}
                onSubmit={handleCreateAndUpload}
                onCancel={() => navigate(getRouteProjects())}
                onFileAdd={handleFileAdd}
                onWebLinkAdd={handleWebLinkAdd}
                isLoading={isLoading}
                isLabs={isLabs}
                selectedModel={selectedModel}
                onModelChange={(value) => setSelectedModel(value as TModelType)}
                skipPrefill={skipPrefill}
                onSkipPrefillChange={setSkipPrefill}
                isUploadingFiles={isUploadingDocs}
                uploadingFilesCount={uploadingFilesCount}
              />
            </div>
          </div>
        </div>
      </div>

      <div
        data-testid="workspace-rooms-root"
        className="h-full w-full min-w-[300px] max-w-[clamp(420px,18vw,720px)] bg-white hidden min-[1440px]:block"
      >
        <div data-testid="workspace-rooms-header" className="w-full h-14 flex items-center justify-between px-4">
          {/* Chat placeholder */}
        </div>
      </div>
    </div>
  );
}
